/** @format */

import { skipToken } from "@reduxjs/toolkit/query";
import React from "react";
import { Button, Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../app/hooks";
import FeatureFlag from "../../featureFlag/FeatureFlag";
import { useGetUserQuery } from "../../../app/services/userApi";
import UserBanner from "../../user/userBanner/UserBanner";
import { useGetResourceLogsQuery } from "../../../app/services/resourcesApi";
import { LastEdited, ResourceReference, UuidString } from "../../../types";

type Props = {
  resource: ResourceReference;
};

const ResourceLogs: React.FC<Props> = ({ resource }) => {
  const { t: _ } = useTranslation();

  const isAuthenticated =
    useAppSelector((state) => state.auth.userToken?.token) || false;
  const { data: user, isFetching: isFetchingCurrentUser } = useGetUserQuery(
    !!isAuthenticated ? undefined : skipToken,
  );

  const { data: resourceLogs, isFetching: isFetchingLogs } =
    useGetResourceLogsQuery(resource.Code, { skip: resource.Type !== "BL" });

  // Function to export log data to JSON file
  const exportData = () => {
    const jsonString = `data:text/json;chatset=utf-8,${encodeURIComponent(
      JSON.stringify(resourceLogs?.Log),
    )}`;
    const link = document.createElement("a");
    link.href = jsonString;
    link.download = `${resource.Code}-logs.json`;

    link.click();
  };

  return (
    <FeatureFlag flag={["dev", "logs"]}>
      <div className="text-center d-flex gap-3 align-items-center justify-content-center">
      {user?.role !== "wjec" || resource.SK.includes("USER_RESOURCE_REF#") ? (
        <>
          {resource.lastEdit?.user?.emailHash &&
            user &&
            !isFetchingCurrentUser &&
            resource.SK.includes("USER_RESOURCE_REF#") && (
              <UserBanner
                userId={resource.lastEdit.user.emailHash as UuidString}
              />
            )}
          <div className="flex-shrink-0">
            <small>
              <span className="fst-italic">{_("Last Modified")}:</span>&nbsp;
              {new Date(resource.lastEdit?.time ?? "").toLocaleDateString()}
            </small>
          </div>
        </>
      ) : isFetchingLogs || (resourceLogs && resourceLogs.Log.length > 0) ? (
        <Dropdown
          drop="down-centered"
          className="d-flex justify-content-center w-100"
        >
          <Dropdown.Toggle
            className="d-flex gap-3 align-items-center bg-transparent border-0"
            id=""
          >
            <UserBanner
              userId={resource.lastEdit?.user?.emailHash as UuidString}
            />
            <div className="flex-shrink-0">
              <small>
                <span className="fst-italic">{_("Last Modified")}:</span>&nbsp;
                {new Date(resource.lastEdit?.time ?? "").toLocaleDateString()}
              </small>
            </div>
          </Dropdown.Toggle>
          <Dropdown.Menu
            className="overflow-auto"
            style={{ maxHeight: "300px" }}
          >
            {isFetchingLogs ? (
              <>
                {[1, 2, 3].map((index) => (
                  <Dropdown.Item key={index}>
                    <div className="placeholder-glow d-flex w-100">
                      <div
                        className="flex-shrink-0 placeholder rounded-circle border-1 border user-image me-3"
                        style={{ width: 40, height: 40 }}
                      ></div>
                      <p className="placeholder my-2">
                        {_("Placeholder name")}
                      </p>
                    </div>
                  </Dropdown.Item>
                ))}
              </>
            ) : (
              <>
                {(resourceLogs?.Log.slice(0, 14) ?? []).map(
                  (log: LastEdited, index: number) => (
                    <Dropdown.Item
                      key={index}
                      className="d-flex justify-content-between gap-3 align-items-center"
                    >
                      {log.user && (
                        <div className="flex-shrink-0">
                          <UserBanner
                            userId={log.user.emailHash as UuidString}
                          />
                        </div>
                      )}
                      <div className="flex-shrink-0">
                        <small>
                          <span className="fst-italic">
                            {_("Last Modified")}:
                          </span>
                          &nbsp;
                          {new Date(log.time).toLocaleString("en-GB", {
                            timeZone: "UTC",
                          })}
                        </small>
                      </div>
                    </Dropdown.Item>
                  ),
                )}
                <div className="w-100 text-center">
                  <Button
                    variant="secondary"
                    className="my-3"
                    onClick={exportData}
                  >
                    {_("Export Full Logs")}
                  </Button>
                </div>
              </>
            )}
          </Dropdown.Menu>
        </Dropdown>
      ) : (
        <>
          <UserBanner
            userId={resource.lastEdit?.user?.emailHash as UuidString}
          />
          <div className="flex-shrink-0">
            <small>
              <span className="fst-italic">{_("Last Modified")}:</span>&nbsp;
              {new Date(resource.lastEdit?.time ?? "").toLocaleDateString()}
            </small>
          </div>
        </>
      )}
      </div>
    </FeatureFlag>
  );
};

export default ResourceLogs;
